import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BookOpen, ShoppingCart, Star, Tablet, Download, Eye } from "lucide-react";
import { ExcerptReader } from "@/components/ExcerptReader";
import { AMAZON_PRINT_URL, AMAZON_EBOOK_URL } from "@/data/constants";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const euros = (c) => (c / 100).toFixed(2).replace(".", ",") + " €";
const BTN = "inline-flex items-center gap-2 rounded-full px-6 py-3 font-ui text-sm tracking-wide transition-colors";

export const FoundationFeature = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const authed = !!user && user !== false;
  const [book, setBook] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [owned, setOwned] = useState(false);
  const [reading, setReading] = useState(false);

  useEffect(() => {
    api.get("/foundation").then((r) => setBook(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!book) return;
    api.get("/carnets").then((r) => setPdf((r.data || []).find((c) => c.book_id === book.id) || null)).catch(() => {});
  }, [book]);
  useEffect(() => {
    if (authed && pdf) api.get("/me/carnets").then((r) => setOwned((r.data || []).some((c) => c.id === pdf.id))).catch(() => {});
  }, [authed, pdf]);

  if (!book) return null;
  const kindle = book.kindle_url || book.ebook_url || AMAZON_EBOOK_URL;
  const amazon = book.amazon_url || AMAZON_PRINT_URL;
  const pdfReady = pdf && pdf.has_pdf && pdf.active_sale;

  const getPdf = async () => {
    if (owned) { window.location.href = `${BACKEND}/api/carnets/${pdf.id}/download`; return; }
    if (!authed) { toast("Connectez-vous pour acheter le PDF"); navigate(`/connexion?next=${window.location.pathname}`); return; }
    try {
      sessionStorage.setItem("tao_unlock_book", `carnet-pdf:${pdf.id}`);
      const { data } = await api.post("/payments/checkout", { product: "carnet", carnet_id: pdf.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      if (e.response?.status === 400) window.location.href = `${BACKEND}/api/carnets/${pdf.id}/download`;
      else toast.error("Paiement impossible pour le moment");
    }
  };

  return (
    <section className="bg-[#1F2A24]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 md:py-20">
        <div
          data-testid="foundation-feature"
          className="rounded-3xl border border-[#C9A24B]/40 bg-gradient-to-br from-[#22302a] to-[#1F2A24] p-8 md:p-12 grid md:grid-cols-[1.4fr_1fr] gap-10 items-center"
        >
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-4 py-1.5 font-ui text-[11px] tracking-[0.18em] font-bold">
              <Star size={13} /> À LIRE EN PREMIER
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#F6F2E9] mt-5">{book.title}</h2>
            <p className="font-sans text-sm md:text-base text-[#F6F2E9]/80 mt-4 leading-relaxed max-w-xl">
              {book.description}
            </p>
            <div className="flex flex-wrap gap-3 mt-7" data-testid="foundation-buttons">
              {book.excerpt_path && (
                <button onClick={() => setReading(true)} data-testid="foundation-excerpt" className={`${BTN} bg-[#F6F2E9] text-[#1F2A24] hover:bg-white`}>
                  <Eye size={16} /> LIRE UN EXTRAIT
                </button>
              )}
              <a href={amazon} target="_blank" rel="noopener noreferrer" data-testid="foundation-amazon" className={`${BTN} bg-[#C9A24B] text-[#1F2A24] hover:bg-[#b8923f]`}>
                <ShoppingCart size={16} /> ACHETER SUR AMAZON
              </a>
              <a href={kindle} target="_blank" rel="noopener noreferrer" data-testid="foundation-kindle" className={`${BTN} border border-[#C9A24B]/60 text-[#F6F2E9] hover:bg-[#C9A24B]/10`}>
                <Tablet size={16} /> LIRE SUR KINDLE
              </a>
              {pdfReady ? (
                <button onClick={getPdf} data-testid="foundation-pdf" className={`${BTN} border border-[#C9A24B]/60 text-[#F6F2E9] hover:bg-[#C9A24B]/10`}>
                  <Download size={16} /> {owned ? "TÉLÉCHARGER MON PDF" : `TÉLÉCHARGER EN PDF · ${euros(pdf.price_cents)}`}
                </button>
              ) : (
                <span data-testid="foundation-pdf-soon" className={`${BTN} border border-[#F6F2E9]/15 text-[#F6F2E9]/45 cursor-default`}>
                  <Download size={16} /> PDF BIENTÔT DISPONIBLE
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-center">
            {book.cover ? (
              <img
                src={book.cover}
                alt={book.title}
                data-testid="foundation-cover"
                className="w-full max-w-[300px] object-contain drop-shadow-xl rounded-xl"
              />
            ) : (
              <div
                className="w-full max-w-[220px] aspect-[3/4] rounded-2xl border border-[#C9A24B]/40 flex flex-col items-center justify-center text-center p-6"
                style={{ background: "linear-gradient(160deg, #385E3A, #2c4a2e)" }}
              >
                <BookOpen className="text-[#C9A24B]" size={40} />
                <p className="font-serif text-lg text-[#F6F2E9] mt-4 leading-snug">
                  Le Tao <span className="text-[#C9A24B]">des</span> 4C
                </p>
                <p className="font-sans text-xs tracking-[0.2em] text-[#F6F2E9]/70 mt-1">FONDEMENTS</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {reading && <ExcerptReader src={`${BACKEND}/api/books/${book.id}/excerpt`} title={book.title} onClose={() => setReading(false)} />}
    </section>
  );
};
